import { Helmet } from 'react-helmet-async'

import { DateTime } from 'luxon'

import { faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import ListBlankState from '@components/ListBlankState'
import PageHeader from '@components/PageHeader'

import { groupEntitiesByEdition } from '@functions/publicationEdition'
import { sanitiseObject } from '@functions/utility'

import { generateUrls } from '@hooks/useOrder'
import useOrders from '@hooks/useOrders'
import useSelector from '@hooks/useSelector'
import useStatuses from '@hooks/useStatuses'
import type { OrderFilters } from '@hooks/useOrders'

import NewsDeskFilters from '@sections/Admin/packs/NewsDesk/components/NewsDeskFilters'

import PublicationEditionGroup from '../PublicationEditionGroup'

const allowedFilters = ['brand_id', 'publication_id', 'publication_edition_id', 'status_id', 'string']

interface PageContentProps {
  pageFilters: OrderFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const sanitisedFilters = sanitiseObject(pageFilters, allowedFilters)

  const ordersPayload = useOrders({
    filters: {
      order_type: 'publication',
      publication_date_start: DateTime.now().minus({ weeks: 2 }).toFormat('yyyy-MM-dd'),
      ...sanitisedFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      // @ts-expect-error TS2561
      include_order_agency: true,
      include_order_campaign: true,
      include_order_item_service_product: true,
      include_order_items: true,
      include_order_pagination_status: true,
      include_order_publication: true,
      include_order_publication_edition: true,
      include_publication_edition_deadlines: true,
      unpaginated_only: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredOrders,
    filteredOrdersCount,
    hasFilteredOrders,
    loading,
  } = ordersPayload

  const statusesPayload = useStatuses({ filters: { status_keys: ['approved', 'pending'] } })
  const { filteredStatuses } = statusesPayload

  const publicationEditions = useSelector(reduxState => reduxState.entities.publicationEditions)

  const groupedOrdersByEdition = groupEntitiesByEdition({
    callbacks: {
      getPrimaryGroupId: entity => entity.publication_edition_id,
      getSecondaryGroupId: entity => entity.agency_id,
    },
    entities: filteredOrders,
    publicationEditions,
    secondaryGroupKey: 'agencies',
  })

  const { requirePaginationUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Require Pagination | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="requirePagination"
        nestedNavigation
        tabBarItems={[
          {
            href: requirePaginationUrl,
            icon: faHome,
            key: 'requirePagination',
            title: 'require pagination',
          },
        ]}
        title="Require Pagination"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredOrders && <ListBlankState />}

            {!loading && hasFilteredOrders && (
              Object.values(groupedOrdersByEdition).map(group => (
                <PublicationEditionGroup
                  key={group.id}
                  editionAgencies={group.agencies}
                  orderCount={group.entityCount}
                  publicationEditionId={group.id}
                />
              ))
            )}

            {!loading && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredOrdersCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NewsDeskFilters
              allowedFilters={allowedFilters}
              pageFilters={pageFilters}
              statuses={filteredStatuses}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
